import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, Link, Text, Thumbnail, Title } from '@nimbus-ds/components';
import { Layout, Page } from '@nimbus-ds/patterns';
import { fetchWithCache, ThirtyMinutesTTL } from '../../utils/cache.utils';

type HuappsListItem = {
  app_slug: string;
  name: { pt: string; en: string; es: string };
  short_description: { pt: string; en: string; es: string };
  thumbnail_url?: string;
  urls: { marketplace_url: string };
  support?: { help_url?: string };
  published?: boolean;
};

type AppsListProps = {
  onClickApp?: (app: HuappsListItem) => void;
  filter?: (app: HuappsListItem) => boolean;
};

const AppsList = ({ onClickApp, filter }: AppsListProps) => {
  const { t, i18n } = useTranslation();
  const [apps, setApps] = useState<HuappsListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const lang = (i18n.language || 'pt') as 'pt' | 'en' | 'es';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = await fetchWithCache<HuappsListItem[]>(
        `rc:apps-list`,
        async () => {
          const base = (
            (process.env.REACT_APP_HUAPPS_API_URI as string) || ''
          ).replace(/\/+$/, '');
          const response = await fetch(`${base}/public/apps/list`);
          const json = (await response.json()) as HuappsListItem[];
          return (json || []).filter((a) => a.published !== false);
        },
        ThirtyMinutesTTL,
      );
      setApps(data);
      setLoading(false);
    };
    load();
  }, []);

  const items = useMemo(
    () => (filter ? apps.filter(filter) : apps),
    [apps, filter],
  );

  return (
    <Page marginTop="6">
      <Page.Body>
        <Layout columns="1">
          <Title as="h4">{t('AppsListPage.Title')}</Title>
          <Layout.Section>
            <Box display="grid" gap="4" gridTemplateColumns="repeat(4, 1fr)">
              {loading
                ? Array.from({ length: 8 }).map((_, idx) => (
                    <Card key={`s-${idx}`}>
                      <Card.Body>
                        <Box
                          display="flex"
                          gap="3"
                          alignItems="center"
                          zIndex="100"
                        >
                          <Thumbnail.Skeleton width="64px" />
                          <Box display="grid" gap="2" width="100%">
                            <Title.Skeleton height="16px" />
                            <Text.Skeleton width="90%" />
                            <Text.Skeleton width="60%" />
                          </Box>
                        </Box>
                      </Card.Body>
                    </Card>
                  ))
                : items.map((app) => (
                    <Card key={app.app_slug} padding="none">
                      <Card.Body padding="none">
                        <Box position="relative" overflow="hidden" zIndex="100">
                          <div
                            style={{
                              position: 'absolute',
                              top: '-40px',
                              right: '-40px',
                              width: '90px',
                              height: '90px',
                              borderRadius: '100%',
                              border: '20px solid #0059d5',
                              zIndex: 1,
                            }}
                          ></div>

                          <Box
                            display="flex"
                            gap="2"
                            alignItems="flex-start"
                            padding="3"
                            position="relative"
                            zIndex="200"
                          >
                            <Box display="grid" gap="1" width="100%">
                              <Title as="h6">
                                <div
                                  style={{
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: '14px',
                                    minHeight: '24px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                  }}
                                >
                                  {app.name[lang]}
                                </div>
                              </Title>
                              <Text fontSize="caption" lineHeight="caption">
                                <div
                                  style={{
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: '12px',
                                    minHeight: '36px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                  }}
                                >
                                  {app.short_description[lang]}
                                </div>
                              </Text>
                              <Box mt="1">
                                <Link
                                  as="a"
                                  appearance="primary"
                                  href={`${app.urls.marketplace_url}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={
                                    onClickApp
                                      ? () => onClickApp(app)
                                      : undefined
                                  }
                                >
                                  {t('AppsListComponent.TryFreePrefix')}
                                  <Text as="span" fontWeight="bold">
                                    {t('AppsListComponent.TryFreeBold')}
                                  </Text>
                                </Link>
                              </Box>
                            </Box>
                            <Box
                              minWidth="92px"
                              position="relative"
                              overflow="hidden"
                            >
                              {app.thumbnail_url ? (
                                <Thumbnail
                                  src={app.thumbnail_url}
                                  alt={app.name[lang]}
                                  width="92px"
                                  height="92px"
                                />
                              ) : (
                                <Thumbnail
                                  alt={app.name[lang]}
                                  width="92px"
                                  height="92px"
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Card.Body>
                    </Card>
                  ))}
            </Box>
          </Layout.Section>
        </Layout>
      </Page.Body>
    </Page>
  );
};

export default AppsList;
